'use client';

import {useEffect, useState} from 'react';

function hasAnalyticsConsent() {
  if (typeof window === 'undefined') return false;
  return window.OnetrustActiveGroups?.includes('V2STACK42') ?? false;
}

export default function GoogleAnalytics({gaId}: {gaId: string}) {
  const [gaLoaded, setGaLoaded] = useState(false);
  const [consentGranted, setConsentGranted] = useState(hasAnalyticsConsent());

  useEffect(() => {
    // Listen for OneTrust consent changes via the OptanonWrapper callback
    const originalWrapper = window.OptanonWrapper;
    window.OptanonWrapper = function () {
      originalWrapper?.();
      setConsentGranted(hasAnalyticsConsent());
    };

    // Also listen for the OneTrust consent change event
    const handleConsentChange = () => {
      setConsentGranted(hasAnalyticsConsent());
    };
    window.addEventListener('consent.onetrust', handleConsentChange);

    return () => {
      window.OptanonWrapper = originalWrapper;
      window.removeEventListener('consent.onetrust', handleConsentChange);
    };
  }, []);

  // Handle consent revocation after GA was already loaded
  useEffect(() => {
    if (gaLoaded && !consentGranted && typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
      });
    }
  }, [gaLoaded, consentGranted]);

  // Inject the gtag script imperatively (no React-rendered <script>) so React 19
  // doesn't warn on locale switch. We guard with a custom marker so we don't
  // double-inject on remount.
  useEffect(() => {
    if (!consentGranted || gaLoaded) return;
    if (document.querySelector('script[data-ga-loader]')) {
      queueMicrotask(() => setGaLoaded(true));
      return;
    }
    const s = document.createElement('script');
    s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    s.async = true;
    s.dataset.gaLoader = '';
    s.onload = () => {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
      window.gtag('consent', 'default', {
        analytics_storage: 'granted',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      });
      window.gtag('config', gaId);
      setGaLoaded(true);
    };
    document.head.appendChild(s);
  }, [consentGranted, gaLoaded, gaId]);

  return null;
}
